import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Tab, Tabs, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { Navigate, useNavigate } from 'react-router-dom';
import { useIsAdmin } from './useIsAdmin';
import { adminUrl, filterParams } from './api';
import { DashboardFilters, EMPTY_FILTERS, Metrics, RenderRow } from './types';
import { Filters } from './Filters';
import { StatCards } from './StatCards';
import { TimeSeriesChart } from './TimeSeriesChart';
import { HistoryTable } from './HistoryTable';
import { AccessLog } from './AccessLog';

export const AdminDashboard: React.FC = () => {
  const { isAdmin, loading: authLoading } = useIsAdmin();
  const navigate = useNavigate();

  const [tab, setTab] = useState<'renders' | 'access'>('renders');
  const [filters, setFilters] = useState<DashboardFilters>(EMPTY_FILTERS);
  const [includeReruns, setIncludeReruns] = useState(true);
  const [bucket, setBucket] = useState<'week' | 'month'>('week');
  // 'chartWeek' = filter/bucket by the chart's date (billing view, default);
  // 'rendered' = by when the render actually ran
  const [dateBasis, setDateBasis] = useState<'chartWeek' | 'rendered'>('chartWeek');
  const [renders, setRenders] = useState<RenderRow[]>([]);
  const [charts, setCharts] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = { ...filterParams(filters), dateBasis };
      const [rendersRes, metricsRes] = await Promise.all([
        fetch(adminUrl('renders', { ...base, includeReruns })),
        fetch(adminUrl('metrics', { ...base, bucket })),
      ]);
      if (rendersRes.status === 403 || metricsRes.status === 403) {
        setError('Not authorized — this dashboard is admin-only.');
        return;
      }
      if (!rendersRes.ok || !metricsRes.ok) {
        throw new Error(`Server error (${rendersRes.status}/${metricsRes.status})`);
      }
      const rendersData = await rendersRes.json();
      const metricsData = await metricsRes.json();
      setRenders(rendersData.renders || []);
      setCharts(rendersData.charts || []);
      setMetrics(metricsData);
    } catch (e: any) {
      setError(e?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [filters, includeReruns, bucket, dateBasis]);

  useEffect(() => {
    if (!authLoading && isAdmin && tab === 'renders') load();
  }, [authLoading, isAdmin, tab, load]);

  if (authLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleOverride = async (id: number, billable: boolean | null, note: string) => {
    const res = await fetch(adminUrl(`renders/${id}/billable`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ billable, note }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to save override');
    }
    await load();
  };

  const downloadCsv = async () => {
    const res = await fetch(adminUrl('report.csv', { ...filterParams(filters), dateBasis }));
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `render-versions_${filters.from || 'start'}_${filters.to || 'now'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openPrintReport = () => {
    const q = new URLSearchParams();
    Object.entries({ ...filterParams(filters), dateBasis }).forEach(([k, v]) => { if (v) q.set(k, v); });
    navigate(`/dashboard/print?${q.toString()}`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Render versions" value="renders" />
        <Tab label="Access log" value="access" />
      </Tabs>

      {tab === 'access' && <AccessLog />}

      {tab === 'renders' && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Render Versions Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<DownloadIcon />} variant="outlined" size="small" onClick={downloadCsv}>
            Export CSV
          </Button>
          <Button startIcon={<PrintIcon />} variant="outlined" size="small" onClick={openPrintReport}>
            Printable report
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Filters
          filters={filters}
          onChange={setFilters}
          charts={charts}
          languages={(metrics?.byLanguage || []).map((b) => b.key)}
          includeReruns={includeReruns}
          onIncludeRerunsChange={setIncludeReruns}
          dateBasis={dateBasis}
          onDateBasisChange={setDateBasis}
        />
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && !metrics && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {metrics && (
        <>
          <StatCards metrics={metrics} />
          <TimeSeriesChart metrics={metrics} bucket={bucket} onBucketChange={setBucket} />
          <Paper variant="outlined">
            <HistoryTable renders={renders} onOverride={handleOverride} />
          </Paper>
        </>
      )}
      </Box>
      )}
    </Box>
  );
};
